import { useState } from 'react';
import { Facebook, Instagram, X, Share2, Youtube, Linkedin, MessageCircle, Send, Twitch, Music2, MapPin, Globe } from 'lucide-react';

// Icon map used by both SocialBadges (public) and admin icon picker
export const SOCIAL_ICON_MAP = {
  instagram: { icon: Instagram, label: 'Instagram', bg: 'bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737]', ring: 'ring-[#E1306C]/40' },
  facebook: { icon: Facebook, label: 'Facebook', bg: 'bg-[#1877F2]', ring: 'ring-[#1877F2]/40' },
  twitter: { icon: X, label: 'X (Twitter)', bg: 'bg-black', ring: 'ring-black/30' },
  pinterest: { icon: MapPin, label: 'Pinterest', bg: 'bg-[#E60023]', ring: 'ring-[#E60023]/40' },
  threads: { icon: MessageCircle, label: 'Threads', bg: 'bg-black', ring: 'ring-black/30' },
  youtube: { icon: Youtube, label: 'YouTube', bg: 'bg-[#FF0000]', ring: 'ring-[#FF0000]/40' },
  linkedin: { icon: Linkedin, label: 'LinkedIn', bg: 'bg-[#0A66C2]', ring: 'ring-[#0A66C2]/40' },
  tiktok: { icon: Music2, label: 'TikTok', bg: 'bg-black', ring: 'ring-black/30' },
  whatsapp: { icon: MessageCircle, label: 'WhatsApp', bg: 'bg-[#25D366]', ring: 'ring-[#25D366]/40' },
  telegram: { icon: Send, label: 'Telegram', bg: 'bg-[#0088CC]', ring: 'ring-[#0088CC]/40' },
  snapchat: { icon: Globe, label: 'Snapchat', bg: 'bg-[#FFFC00]', ring: 'ring-[#FFFC00]/40', dark: true },
  twitch: { icon: Twitch, label: 'Twitch', bg: 'bg-[#9146FF]', ring: 'ring-[#9146FF]/40' },
  discord: { icon: MessageCircle, label: 'Discord', bg: 'bg-[#5865F2]', ring: 'ring-[#5865F2]/40' },
  tripadvisor: { icon: MapPin, label: 'TripAdvisor', bg: 'bg-[#00AF87]', ring: 'ring-[#00AF87]/40' },
  yelp: { icon: Star, label: 'Yelp', bg: 'bg-[#D32323]', ring: 'ring-[#D32323]/40' },
  website: { icon: Globe, label: 'Site web', bg: 'bg-garden-700', ring: 'ring-garden-700/40' },
};

import { Star } from 'lucide-react';

const BUILTIN_SOCIALS = [
  { key: 'instagram', settingKey: 'social_instagram', showKey: 'show_instagram' },
  { key: 'facebook', settingKey: 'social_facebook', showKey: 'show_facebook' },
  { key: 'pinterest', settingKey: 'social_pinterest', showKey: 'show_pinterest' },
  { key: 'twitter', settingKey: 'social_twitter', showKey: 'show_twitter' },
  { key: 'threads', settingKey: 'social_threads', showKey: 'show_threads' },
];

export default function SocialBadges({ settings }) {
  const [open, setOpen] = useState(false);

  if (!settings || settings.showSocialBadges === false) return null;

  const networks = [];

  for (const s of BUILTIN_SOCIALS) {
    const url = settings[s.settingKey];
    const show = settings[s.showKey] !== false;
    if (show && url) {
      const info = SOCIAL_ICON_MAP[s.key] || SOCIAL_ICON_MAP.website;
      networks.push({ key: s.key, url, ...info });
    }
  }

  const customs = settings.custom_socials || [];
  for (const c of customs) {
    if (c.url) {
      const info = SOCIAL_ICON_MAP[c.icon_key] || SOCIAL_ICON_MAP.website;
      networks.push({ key: `custom-${c.name}`, url: c.url, label: c.name, icon: info.icon, bg: info.bg, ring: info.ring, dark: info.dark });
    }
  }

  if (networks.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50" data-testid="social-badges">
      {/* Expanded icons */}
      <div className={`flex flex-col-reverse gap-3 mb-3 transition-all duration-300 ${open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'}`}>
        {networks.map((n) => {
          const Icon = n.icon || Globe;
          return (
            <a
              key={n.key}
              href={n.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`group relative flex items-center justify-center w-11 h-11 ${n.bg} rounded-full shadow-lg ring-2 ${n.ring || 'ring-white/30'} hover:scale-110 hover:shadow-xl transition-all duration-200`}
              title={n.label}
              data-testid={`social-badge-${n.key}`}
            >
              <Icon size={18} className={n.dark ? 'text-black' : 'text-white'} />
              <span className="absolute left-[52px] bg-charcoal/90 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-md">
                {n.label}
              </span>
            </a>
          );
        })}
      </div>

      {/* FAB toggle */}
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg ring-2 ring-white/30 hover:shadow-xl transition-all duration-300 ${open ? 'bg-charcoal ring-charcoal/30 rotate-45' : 'bg-garden-800 ring-garden-800/30 rotate-0'}`}
        data-testid="social-fab-toggle"
        aria-label="Réseaux sociaux"
      >
        <Share2 size={20} className="text-white" />
      </button>
    </div>
  );
}
